import React, { useState } from 'react';
import { Button, Segmented, Spin, Tag, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import FacturasManager from '../components/facturacion/FacturasManager';
import FacturacionMasivaTab from '../components/facturacion/FacturacionMasivaTab';
import ClientesFiscalesManager from '../components/facturacion/ClientesFiscalesManager';
import CrearFacturaModal from '../components/facturacion/CrearFacturaModal';
import { useGetStampBalanceQuery } from '../services/stripeApi';
import { useResponsive } from '../hooks/useResponsive';
import { useAppSelector } from '../hooks/redux';
import { canManageFacturas, isSupportUser } from '../utils/roleHelpers';
import '../components/facturacion/facturacion.css';

type TabKey = 'facturas' | 'masiva' | 'clientes';

const Facturacion: React.FC = () => {
  const { isMobile } = useResponsive();
  const { data: stampBalance, isLoading: loadingStamps } = useGetStampBalanceQuery();
  const [activeTab, setActiveTab] = useState<TabKey>('facturas');
  const { user } = useAppSelector((state) => state.auth);
  // La facturación masiva (y la global) es de administración; el backend lo exige.
  const puedeGestionar = canManageFacturas(user?.role, isSupportUser(user?.email));
  const [crearFacturaOpen, setCrearFacturaOpen] = useState(false);
  // Señal para que ClientesFiscalesManager abra su modal de alta desde la barra
  const [addClienteSignal, setAddClienteSignal] = useState(0);

  const renderStamps = () => {
    if (loadingStamps) return <Spin size="small" />;

    if (!stampBalance?.facturacionActiva) {
      return <Tag>Facturación no configurada</Tag>;
    }

    const sinTimbres = !stampBalance.puedeTimbrar || stampBalance.totalDisponibles === 0;
    const tone = sinTimbres ? 'fac-stamps--bad' : stampBalance.alertaActiva ? 'fac-stamps--warn' : '';
    const planPct = stampBalance.timbresPlanMensual > 0
      ? Math.min(100, (stampBalance.timbresPlanDisponibles / stampBalance.timbresPlanMensual) * 100)
      : 0;

    // El número grande es lo que se puede timbrar; el detalle desglosa de dónde sale, y
    // plan + extra tiene que sumar ese total. Por eso se usan los extras TOTALES: los del
    // pool antiguo también se consumen, y mostrar solo los de la sucursal dejaba un
    // desglose que no cuadraba ("Puedes timbrar 285 · Plan 265 · Extra 0").
    const extrasDisponibles = stampBalance.timbresExtraTotalDisponibles
      ?? (stampBalance.timbresExtraDisponibles + (stampBalance.timbresExtraPoolDisponibles ?? 0));

    // El conteo puede no venir si el Facturador desplegado es anterior al campo; sin esta
    // guarda la UI diría "compartidos entre 0 sucursales".
    const sucursales = (stampBalance.cuentasVinculadas ?? 0) >= 2
      ? `${stampBalance.cuentasVinculadas} sucursales`
      : 'varias sucursales';

    const tooltip = [
      `Puedes timbrar ${stampBalance.totalDisponibles}`,
      `Plan: ${stampBalance.timbresPlanDisponibles} de ${stampBalance.timbresPlanMensual}`
        + (stampBalance.esCompartido ? ` (compartidos entre ${sucursales})` : ''),
      `Extra: ${extrasDisponibles}`,
    ].join(' · ');

    return (
      <Tooltip title={tooltip}>
        <div className={`fac-stamps ${tone}`}>
          <span className="fac-stamps__num">{stampBalance.totalDisponibles}</span>
          <span className="fac-stamps__label">
            timbres
            <span className="fac-stamps__bar"><i style={{ width: `${planPct}%` }} /></span>
          </span>
          <span className="fac-stamps__detail">
            {stampBalance.timbresPlanDisponibles} plan
            {extrasDisponibles > 0 && ` · ${extrasDisponibles} extra`}
          </span>
        </div>
      </Tooltip>
    );
  };

  return (
    <div>
      <div className="fac-bar">
        <h2 className="fac-bar__title">Facturación</h2>
        <Segmented
          value={activeTab}
          onChange={(value) => setActiveTab(value as TabKey)}
          options={[
            { label: 'Facturas', value: 'facturas' },
            ...(puedeGestionar ? [{ label: isMobile ? 'Masiva' : 'Facturación masiva', value: 'masiva' }] : []),
            { label: 'Clientes', value: 'clientes' },
          ]}
        />
        <div className="fac-bar__spacer" />
        {renderStamps()}
        {activeTab === 'facturas' && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCrearFacturaOpen(true)}>
            {!isMobile && 'Nueva factura'}
          </Button>
        )}
        {activeTab === 'clientes' && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddClienteSignal((s) => s + 1)}>
            {!isMobile && 'Agregar cliente'}
          </Button>
        )}
      </div>

      {activeTab === 'facturas' && <FacturasManager />}
      {activeTab === 'masiva' && puedeGestionar && <FacturacionMasivaTab />}
      {activeTab === 'clientes' && <ClientesFiscalesManager addSignal={addClienteSignal} />}

      <CrearFacturaModal
        visible={crearFacturaOpen}
        onCancel={() => setCrearFacturaOpen(false)}
        onSuccess={() => setCrearFacturaOpen(false)}
      />
    </div>
  );
};

export default Facturacion;
